// Запуски: старт, снимок, отмена и поток этапов.
//
// Здесь же живёт переподключение, и это не украшение. Обещание экрана 05 —
// «можно закрыть», а сервер его держит: уход клиента запуск не отменяет
// (apps/api/src/routes/runs.ts). Значит клиент обязан уметь вернуться.

import { requestText } from '@smyslika/core'
import type {
  Artifact,
  ClarificationPair,
  RatingReason,
  RatingVerdict,
  Refusal,
  Run,
  RunStatus,
  RunStep,
  SafetyNotice,
  ScenarioId,
  ScenarioOffer,
  Source,
  UnitId,
} from '@smyslika/core'

import type { Client } from './client.ts'
import { ApiError, OfflineError } from './client.ts'

/** Исходы запуска. Те же слова, что пишет сервер в журнал. */
export type RunOutcome = 'done' | 'cancelled' | 'failed' | 'blocked'

/** Состояние запуска целиком — то же, что отдаёт `GET /v1/runs/:id`. */
export interface RunState extends Run {
  /** Накопленный текст: переподключение не начинает с нуля. */
  text: string
  /**
   * Чем кончился запуск. Четыре исхода, и различать их обязательно.
   *
   * `blocked` — поставщик отказался по содержанию запроса; это не сбой, и
   * повтором он не лечится. Пока здесь стояло `'ok' | 'failed' | 'cancelled'`,
   * такой запуск попадал в ветку «иначе» и подписывался «Готово» — то есть
   * отказ выглядел успехом. Успех при этом зовётся `done`, а не `ok`:
   * `ok` сервер не отдавал никогда.
   */
  outcome?: RunOutcome
  charged: boolean
  artifact?: Artifact
  offer?: ScenarioOffer
  /**
   * Границы ответственности на этой теме, в порядке показа. SMY-77
   *
   * Приезжают с сервера готовым текстом, а не признаком темы: клиент не
   * должен знать, какими словами продукт называет границу, — иначе тексты
   * разойдутся между вебом и телефоном.
   */
  notices?: SafetyNotice[]
  /**
   * Почему отказали. Есть только у `outcome: 'blocked'` по нашему правилу
   * (SMY-78); отказ поставщика своей формулировки не приносит.
   */
  refusal?: Refusal
  /** Остаток по единице после списания. Только у списавшего запуска. */
  remaining?: number
}

export type RunEvent =
  | { type: 'state'; run: RunState }
  | { type: 'step'; step: RunStep }
  | { type: 'source'; source: Source }
  | { type: 'delta'; text: string }
  | { type: 'artifact'; artifact: Artifact }
  | { type: 'offer'; offer: ScenarioOffer }
  | { type: 'notice'; notice: SafetyNotice }
  | { type: 'done'; outcome: RunOutcome; charged: boolean; remaining?: number }
  /** Такого запуска у сервера нет. Не обрыв связи — возвращаться некуда. */
  | { type: 'gone' }

export interface StartRunInput {
  text: string
  scenario: ScenarioId
  chatId?: string
  fileIds?: string[]
  chosen?: ScenarioId
  /** Ключ решения классификатора, полученный от `/v1/intent`. SMY-124 */
  intentId?: string
  /**
   * Ответы на уточнения парами «вопрос — ответ». SMY-487
   *
   * Отдельно от `text`: модель получает их блоком «Уточнения» с вопросами, а
   * не склейкой «Только официальные. Сегодня». Собирает текст сервер тем же
   * правилом, что `sentText` ниже.
   */
  clarifications?: ClarificationPair[]
  /**
   * Запуск начат кнопкой «Изменить» под ответом этого запуска. SMY-497
   *
   * Только для счёта доли правок допущений: бесплатность и списание сервер
   * решает без него, чужой запуск он не запишет.
   */
  assumptionOf?: string
}

/**
 * Текст, которым вопрос встанет в ленту, — запрос с блоком «Уточнения».
 * Пузырь отправленного сверяется с лентой точным равенством (`unseenBubbles`),
 * поэтому собирается тем же правилом ядра, что и на сервере, а не своим.
 */
export const sentText = (input: Pick<StartRunInput, 'text' | 'clarifications'>): string =>
  requestText(input.text, input.clarifications ?? [])

export interface StartedRun {
  runId: string
  chatId?: string
  /**
   * Запуск ничего не спишет: это уточнение в уже оплаченном чате.
   *
   * Решает сервер, и поле приходит оттуда, а не выводится клиентом: правило
   * «молча не списываем» работает в обе стороны — молча не дарим тоже, и
   * лента объявляет бесплатность до результата.
   */
  free?: boolean
}

/** Отказ по остатку: 402 с разобранными полями для экрана 02. */
export interface NotEnoughUnits {
  reason: 'exhausted' | 'not_in_tariff'
  unit: UnitId
  remaining: number
  limit: number
  until?: string
  message: string
}

export const asNotEnoughUnits = (e: unknown): NotEnoughUnits | null => {
  if (!(e instanceof ApiError) || e.status !== 402) return null
  const b = e.body
  return {
    reason: b.reason === 'not_in_tariff' ? 'not_in_tariff' : 'exhausted',
    unit: b.unit as UnitId,
    remaining: typeof b.remaining === 'number' ? b.remaining : 0,
    limit: typeof b.limit === 'number' ? b.limit : 0,
    ...(typeof b.until === 'string' ? { until: b.until } : {}),
    message: e.message,
  }
}

/** Что делает поток, когда связь рвётся. */
export interface StreamHandlers {
  onEvent: (event: RunEvent) => void
  /**
   * Связь потеряна и мы ждём восстановления. `attempt` растёт с каждой
   * попыткой; экран 05 показывает по нему «связь пропала, пробую снова».
   */
  onReconnecting?: (attempt: number) => void
  /** Поток снова идёт. Первым событием после этого придёт `state`. */
  onOpen?: () => void
}

/** Пауза между попытками: 1, 2, 4, 8, но не дольше 15 секунд. */
const backoff = (attempt: number): number => Math.min(1000 * 2 ** (attempt - 1), 15_000)

/**
 * Что делалось за запуск — «детали результата» за кнопкой «ещё».
 *
 * Поставщик и модель приходят, ТОЛЬКО если человек попросил их тоглом на
 * экране 11. Решает это сервер: то, чего он не отдал, нельзя показать по
 * ошибке.
 */
export interface RunDetails {
  scenario: ScenarioId
  unit: UnitId
  outcome: string | null
  startedAt: string | null
  finishedAt: string | null
  steps: {
    label: string
    status: string
    durationMs: number | null
    /** Кто выполнил. Имя или нейтральная замена — решает сервер по договору. */
    provider?: string
    /** Имя модели. Приходит только там, где называть его разрешено. */
    model?: string
    /** Где обрабатывались данные этого этапа. */
    region?: string
  }[]
  /**
   * Брались ли сведения из интернета. SMY-79
   *
   * Полей прозрачности нет вовсе, когда тогл выключен, — а не приходят
   * пустыми. Разница существенная: по отсутствию поля видно, что человек не
   * просил, а по `false` читалось бы «источников не было».
   */
  externalSources?: boolean
  /** Была ли независимая проверка другим поставщиком. */
  independentPass?: boolean
}

/** Что человек поставил результату. SMY-123 */
export interface RatingInput {
  verdict: RatingVerdict
  /** Почему плохо. Только у плохой оценки — у хорошей сервер её отбросит. */
  reason?: RatingReason
  /**
   * Свободный текст. СОДЕРЖИМОЕ: не читается подряд, открывается по причине и
   * с записью в журнал (SMY-127). Клиент об этом знать не обязан, но и
   * посылать сюда что попало не должен — это поле отзыва, а не второй запрос.
   */
  comment?: string
}

export interface SavedRating {
  runId: string
  verdict: RatingVerdict
  reason: RatingReason | null
  hasComment: boolean
  createdAt: string
}

export interface Runs {
  /**
   * Завести запуск. `key` — ключ попытки (`newAttemptKey` в ядре, SMY-449):
   * повтор с тем же ключом получает тот же запуск. Отдельным аргументом, а не
   * полем входа: вход хранится для повтора сбоя, и ключ, попавший туда, отдавал
   * бы на каждый повтор тот же сбойный запуск. `signal` обрывает ожидание
   * ответа: повтор, который экран бросил, не должен занять запуск.
   */
  start(input: StartRunInput, key?: string, signal?: AbortSignal): Promise<StartedRun>
  /**
   * Оценить результат. Повторный вызов заменяет прежнюю оценку: у запуска она
   * одна, последняя, — человек, передумавший через минуту, не второй голос.
   */
  rate(runId: string, input: RatingInput): Promise<SavedRating>
  /** Детали запуска. Живут в базе, а не в памяти движка: их открывают и через неделю. */
  details(runId: string): Promise<RunDetails>
  snapshot(runId: string): Promise<RunState | null>
  cancel(runId: string): Promise<boolean>
  /**
   * Подписка на поток. Возвращает функцию отписки.
   *
   * Переподключается сама, пока её не остановят или пока сервер не скажет
   * `gone`. Терминальный статус тоже останавливает: досматривать нечего.
   */
  stream(runId: string, handlers: StreamHandlers): () => void
}

export function createRuns(client: Client): Runs {
  return {
    async start(input, key, signal) {
      return client.request<StartedRun>('/v1/runs', {
        method: 'POST',
        body: key != null ? { ...input, key } : input,
        ...(signal != null ? { signal } : {}),
      })
    },

    async details(runId) {
      return client.request<RunDetails>(`/v1/runs/${encodeURIComponent(runId)}/details`)
    },

    async rate(runId, input) {
      const res = await client.request<{ rating: SavedRating }>(
        `/v1/runs/${encodeURIComponent(runId)}/rating`,
        { method: 'POST', body: input },
      )
      return res.rating
    },

    async snapshot(runId) {
      try {
        return await client.request<RunState>(`/v1/runs/${encodeURIComponent(runId)}`)
      } catch (e) {
        // 404 — не ошибка вызывающего: запуск мог завершиться и уйти из
        // памяти сервера, пока вкладка была закрыта.
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },

    async cancel(runId) {
      const res = await client.request<{ cancelled: boolean }>(
        `/v1/runs/${encodeURIComponent(runId)}/cancel`,
        { method: 'POST' },
      )
      return res.cancelled
    },

    stream(runId, handlers) {
      let stopped = false
      let attempt = 0
      let source: EventSource | null = null
      let timer: ReturnType<typeof setTimeout> | null = null

      const close = (): void => {
        source?.close()
        source = null
      }

      const retry = (): void => {
        attempt += 1
        handlers.onReconnecting?.(attempt)
        timer = setTimeout(() => void connect(), backoff(attempt))
      }

      const connect = async (): Promise<void> => {
        if (stopped) return
        if (client.tokens.get() == null) return

        // Билет, а не токен сеанса (SMY-221). EventSource заголовков не умеет, и
        // поток пускает по параметру запроса, а адрес оседает в журналах и
        // прокси. Билет гаснет при первом предъявлении и живёт минуту — поэтому
        // берётся заново на каждое подключение, и на переподключение тоже.
        let ticket: string
        try {
          const issued = await client.request<{ ticket: string }>(
            `/v1/runs/${encodeURIComponent(runId)}/ticket`,
            { method: 'POST' },
          )
          ticket = issued.ticket
        } catch (e) {
          if (stopped) return
          // Запуска нет — то же, что сказал бы поток: досматривать нечего.
          if (e instanceof ApiError && e.status === 404) {
            stopped = true
            handlers.onEvent({ type: 'gone' })
            return
          }
          // Сеанса нет — переподключаться незачем, как и без токена.
          if (e instanceof ApiError && e.status === 401) return
          retry()
          return
        }
        if (stopped) return

        const url =
          `${client.baseUrl}/v1/runs/${encodeURIComponent(runId)}/events` +
          `?ticket=${encodeURIComponent(ticket)}`
        const es = new EventSource(url)
        source = es

        const deliver = (type: RunEvent['type']) => (e: MessageEvent<string>) => {
          if (stopped) return
          if (attempt !== 0) {
            attempt = 0
            handlers.onOpen?.()
          }
          let event: RunEvent
          try {
            event = { ...(JSON.parse(e.data) as object), type } as RunEvent
          } catch {
            return
          }
          handlers.onEvent(event)

          // Сервер сказал, что запуска нет, либо он завершился — досматривать
          // нечего, и переподключаться тем более.
          if (event.type === 'gone' || event.type === 'done') {
            stopped = true
            close()
          }
        }

        for (const type of ['state', 'step', 'source', 'delta', 'artifact', 'offer', 'done', 'gone'] as const) {
          es.addEventListener(type, deliver(type) as EventListener)
        }

        es.onopen = () => {
          if (attempt !== 0) {
            attempt = 0
            handlers.onOpen?.()
          }
        }

        es.onerror = () => {
          if (stopped) return
          close()
          retry()
        }
      }

      void connect()

      return () => {
        stopped = true
        if (timer != null) clearTimeout(timer)
        close()
      }
    },
  }
}

export { ApiError, OfflineError }
